import React from 'react';
import { motion } from 'framer-motion'; // Added for new sections
import WhatsAppConnect from '../components/whatsappConnect'; // Corrected casing
import InquiryButton from '../components/InquiryButton'; // Corrected casing

// Reusable Navbar Component
const PageNavbar = () => {
  return (
    <nav className="fixed top-0 w-full bg-white shadow-md z-50 flex items-center justify-between px-6 h-24 overflow-hidden">
      <img
        src="/Hydrocawach_logo.png"
        alt="HydroCawach Logo"
        className="h-36 w-auto object-contain"
      />
      <h1 className="absolute left-1/2 transform -translate-x-1/2 text-blue-600 text-lg md:text-2xl font-bold">
        Advanced Fluid Dynamics Technology
      </h1>
    </nav>
  );
};

// --- START: Data for new sections ---
const cosmeticApplications = [
  { title: 'Mixing & Homogenization', description: 'Achieves nano-scale uniformity for creams, gels, and lotions.' },
  { title: 'Solubilization', description: 'Enhances dissolution of actives, fragrances, and colorants.' },
  { title: 'Hydration of Polymers', description: 'Rapid activation of thickeners, gums, and stabilizers.' },
  { title: 'Emulsion Formation', description: 'Produces stable O/W and W/O emulsions with fine droplet size.' },
  { title: 'Functionalization', description: 'Improves bioavailability and performance of cosmetic ingredients.' },
];

const cosmeticAdvantages = [
  { icon: '⚡', title: 'High Efficiency', description: 'Reduces processing time and energy use.' },
  { icon: '💧', title: 'Superior Product Quality', description: 'Uniform texture, gloss, and stability.' },
  { icon: '🌱', title: 'Eco-Friendly', description: 'Minimizes chemical emulsifiers and thermal load.' },
  { icon: '🔄', title: 'Continuous & Scalable', description: 'Ideal for both lab-scale and production setups.' },
  { icon: '⚙️', title: 'Low Maintenance', description: 'Compact, plug-and-play operation.' },
];
// --- END: Data for new sections ---

const CavitationTechnology = () => {
  return (
    <div className="bg-white text-gray-800 min-h-screen relative overflow-x-hidden">
      <PageNavbar />

      <div className="pt-28 px-4 max-w-5xl mx-auto">

        {/* --- EXISTING VIDEO --- */}
        <video
          src="/vid3.mp4"
          controls
          className="w-full rounded-lg shadow-lg mb-8"
        />

        {/* --- START: NEW CONTENT ADDED HERE --- */}
        <section className="space-y-12 mb-12">

          {/* NEW: Edible Oils Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-8 shadow-md"
          >
            <h3 className="text-2xl font-semibold text-yellow-800 mb-4">🛢️ Hydrocawach Cavitation Systems for Edible Oils</h3>

            <p className="text-lg text-gray-700">
              Hydrocawach’s cavitation-based oil refining
              systems provide a next-generation approach to chemical process
              intensification. Our modular units deliver high-performance refining
              with lower resource use, enabling producers to achieve premium-quality
              edible oils with greater energy and cost efficiency.
            </p>
          </motion.div>

          {/* NEW: Cosmetics Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-pink-50 border-l-4 border-pink-500 rounded-r-lg p-8 shadow-md"
          >
            <h3 className="text-2xl font-semibold text-pink-800 mb-4">💄 Cosmetics & Personal Care</h3>

            <p className="text-lg text-gray-700 mb-6">
              Cavitation technology transforms liquid processing in the cosmetic and personal care
              industry by enabling efficient mixing, homogenization, solubilization,
              and emulsion stabilization — all in a single, energy-efficient step.
            </p>
            <p className="text-lg text-gray-700 mb-8">
              Through hydrodynamic cavitation, microbubbles form and collapse within
              the fluid, generating intense shear, turbulence, and localized energy release.
              This action ensures uniform dispersion of ingredients, rapid hydration of
              thickeners and stabilizers, and production of highly stable emulsions
              without the need for mechanical agitators or excessive heat.
            </p>

            {/* Grid for Applications and Advantages */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Applications Column */}
              <div>
                <h4 className="text-xl font-bold text-pink-700 mb-4">Applications</h4>
                <ul className="space-y-4">
                  {cosmeticApplications.map((app) => (
                    <li key={app.title} className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-pink-500 flex-shrink-0 mr-2 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <span className="font-semibold text-gray-800">{app.title}:</span>
                        <p className="text-gray-700">{app.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Advantages Column */}
              <div>
                <h4 className="text-xl font-bold text-pink-700 mb-4">Advantages</h4>
                <ul className="space-y-4">
                  {cosmeticAdvantages.map((adv) => (
                    <li key={adv.title} className="flex items-start">
                      <span className="text-2xl flex-shrink-0 mr-2">{adv.icon}</span>
                      <div>
                        <span className="font-semibold text-gray-800">{adv.title}:</span>
                        <p className="text-gray-700">{adv.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </section>
        {/* --- END: NEW CONTENT --- */}


        {/* --- EXISTING CONTENT (INTACT) --- */}
        <section className="text-lg space-y-4">
          <p>
            At Hydrocawach Technologies Pvt. Ltd., we harness the power of Advanced Fluid Dynamics Technology—
            a cutting-edge, chemical-free, and energy-efficient technology revolutionizing water and wastewater treatment.
          </p>

          <h2 className="text-2xl font-semibold text-blue-600 mt-6 mb-4">How It Works</h2>

          <div className="flex flex-col md:flex-row gap-6 items-center">
            {/* Text section */}
            <div className="md:w-1/2 space-y-4">
              <p>
                Advanced Fluid Dynamics Technology involves the controlled formation, growth, and violent collapse of nano bubbles in water.
                When water passes through specially designed Advanced Fluid Dynamics Technological devices like venturi tubes or orifice plates at high velocity,
                nano bubbles collapse violently, producing intense microjets and shockwaves.
              </p>

              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Microbial Destruction:</strong> Physically disrupts microbial cells, reducing pathogens like E. Coli.</li>
                <li><strong>Reduction in BOD:</strong> Destroys organic contaminants and microorganisms, improving oxygen levels.</li>
                <li><strong>OH Radical Generation:</strong> Produces reactive radicals that break down complex pollutants, reducing COD.</li>
                <li><strong>Algae Removal:</strong> Breaks algal cells for visibly clearer and healthier water bodies.</li>
              </ul>
            </div>

            {/* Image section */}
            <div className="md:w-1/2 flex justify-center">
              <img
                src="/pump_working.png"
                alt="Pump Working Diagram"
                Now className="w-full max-w-md rounded-lg shadow-md object-contain"
                Ind />
            </div>
          </div>


          <h2 className="text-2xl font-semibold text-blue-600 mt-6">Why Choose Hydrocawach's Advanced Fluid Dynamics Technology?</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>⚡ No Chemicals, No Filters</li>
            <li>⚙️ Low Operational Costs</li>
            <li>♻️ Eco-Friendly and Sustainable</li>
            <li>🔬 Scientifically Proven and Field-Tested</li>
          </ul>

          <p className="mt-6">
            Whether its for handpump disinfection, lake rejuvenation, or industrial water, our Advanced Fluid Dynamics Technology is a powerful solution for clean, safe, and sustainable water.
          </p>
        </section>
      </div>

      {/* Floating Components */}
      <WhatsAppConnect />

    </div>
  );
};

export default CavitationTechnology;